import { DateTime } from '@cheqroom/date-time';

import { type DynamicDateFilter, type DynamicFilterValue, getDateQuery, parseDynamicFilters } from '../../filters';

/**
 * Reservation list / search status values accepted by {@link buildReservationSearchFilters}.
 */
export enum ReservationStatusFilter {
	DRAFT = 'DRAFT',
	OPEN = 'OPEN',
	CONVERTED = 'CONVERTED',
	CLOSED = 'CLOSED',
	CANCELLED = 'CANCELLED',
	OVERDUE = 'OVERDUE',
	ARCHIVED = 'ARCHIVED',
}

const statusToListNameMap: Record<string, string> = {
	[ReservationStatusFilter.DRAFT]: 'creating',
	[ReservationStatusFilter.OPEN]: 'open',
	[ReservationStatusFilter.CONVERTED]: 'closed',
	[ReservationStatusFilter.CLOSED]: 'closed_manually',
	[ReservationStatusFilter.CANCELLED]: 'cancelled',
	[ReservationStatusFilter.OVERDUE]: 'overdue',
	[ReservationStatusFilter.ARCHIVED]: 'archived',
};

export type ReservationListName = [string, 'or' | 'and'];

export type ReservationSearchFiltersQuery = {
	listNames?: ReservationListName[];
	fromBetween?: string;
	toBetween?: string;
	fromDate__gte?: string;
	toDate__lte?: string;
	location__in?: string[];
	label__in?: string[];
	customer__in?: string[];
	repeatId__exists?: boolean;
	repeatId__in?: string[];
	[key: string]: unknown;
};

export type BuildReservationSearchFiltersInput = {
	statuses?: string[];
	maintenance?: 'maintenance' | 'no_maintenance';
	conflicts?: 'with_conflicts' | 'no_conflicts';
	dateRange?: {
		start?: string;
		end?: string;
	};
	locationIds?: string[];
	labelIds?: string[];
	customerIds?: string[];
	repeating?: 'repeating' | 'non_repeating';
	repeatIds?: string[];
	closed?: DynamicDateFilter;
	created?: DynamicDateFilter;
	cancelled?: DynamicDateFilter;
	/** Extra field filters keyed by field id. */
	dynamicFilters?: Record<string, DynamicFilterValue | undefined>;
};

/**
 * Builds Python reservation search / `customFieldFilters` params from neutral filter ids.
 * UI adapters should unwrap chip shapes before calling this.
 */
export const buildReservationSearchFilters = (
	input: BuildReservationSearchFiltersInput = {}
): ReservationSearchFiltersQuery => {
	const {
		statuses,
		maintenance,
		conflicts,
		dateRange,
		locationIds,
		labelIds,
		customerIds,
		repeating,
		repeatIds,
		closed,
		created,
		cancelled,
		dynamicFilters = {},
	} = input;

	const listNames: ReservationListName[] = [];

	const hasStatuses = Boolean(statuses && statuses.length > 0);

	if (!hasStatuses && !maintenance && !conflicts) {
		listNames.push(['unarchived', 'or']);
	}

	if (hasStatuses && statuses) {
		for (const status of statuses) {
			const listName = statusToListNameMap[status];
			if (listName) {
				listNames.push([listName, 'or']);
			}
		}
	}

	if (maintenance === 'maintenance') {
		listNames.push(['maintenance', 'and']);
	}

	if (maintenance === 'no_maintenance') {
		listNames.push(['no_maintenance', 'and']);
	}

	if (conflicts === 'with_conflicts') {
		listNames.push(['conflicting', 'and']);
	}

	if (conflicts === 'no_conflicts') {
		listNames.push(['nonconflicting', 'and']);
	}

	let query: ReservationSearchFiltersQuery = {
		listNames,
	};

	if (dateRange) {
		const { start, end } = dateRange;

		if (start && end) {
			query.fromBetween = DateTime.fromISOString(start).startOfDay().toFloatingISOString();
			query.toBetween = DateTime.fromISOString(end).endOfDay().toFloatingISOString();
		} else {
			if (start) {
				query.fromDate__gte = DateTime.fromISOString(start).startOfDay().toFloatingISOString();
			}
			if (end) {
				query.toDate__lte = DateTime.fromISOString(end).endOfDay().toFloatingISOString();
			}
		}
	}

	if (locationIds && locationIds.length > 0) {
		query.location__in = locationIds;
	}

	if (labelIds && labelIds.length > 0) {
		query.label__in = labelIds;
	}

	if (customerIds && customerIds.length > 0) {
		query.customer__in = customerIds;
	}

	if (repeating) {
		query.repeatId__exists = repeating === 'repeating';
	}

	if (repeatIds && repeatIds.length > 0) {
		query.repeatId__in = repeatIds;
	}

	if (closed) {
		query = { ...query, ...getDateQuery('closed', closed) };
	}

	if (created) {
		query = { ...query, ...getDateQuery('created', created) };
	}

	if (cancelled) {
		query = { ...query, ...getDateQuery('cancelled', cancelled) };
	}

	return {
		...query,
		...parseDynamicFilters(dynamicFilters),
	};
};
